import { logEvent } from "@src/appV2/lib/analytics/log";
import { showInfoToast } from "@src/appV2/lib/Notifications";
import { type Dispatch, type SetStateAction, useEffect } from "react";

import { EXPIRY_CHECK_INTERVAL_MS, EXPIRY_MS, IMPERSONATION_USER_EVENTS } from "../constants";
import type { ImpersonationSession, ImpersonationSessionType } from "../types";

export function useSessionExpiry(
  sessions: ImpersonationSession[],
  setSessions: Dispatch<SetStateAction<ImpersonationSession[]>>,
  employeeId: string | undefined
): void {
  useEffect(() => {
    if (sessions.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const expiredSessions: Array<{
        employeeId: string | undefined;
        targetId: string;
        targetName: string;
        sessionType: ImpersonationSessionType;
        sessionId: string;
      }> = [];

      setSessions((previous) => {
        const kept: ImpersonationSession[] = [];
        for (const session of previous) {
          const isExpired = session.expiresAt
            ? now >= session.expiresAt
            : now - session.startedAt >= EXPIRY_MS;
          if (isExpired && session.isActive) {
            expiredSessions.push({
              employeeId,
              targetId: session.targetId,
              targetName: session.targetName,
              sessionType: session.sessionType,
              sessionId: session.id,
            });
            // Workplace sessions cannot be refreshed — auto-close on expiry
            if (session.sessionType === "workplace") {
              continue;
            }

            kept.push({ ...session, isActive: false, isMinimized: true });
          } else {
            kept.push(session);
          }
        }

        return kept;
      });

      for (const expired of expiredSessions) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_EXPIRED, expired);
        if (expired.sessionType === "workplace") {
          showInfoToast(`Impersonation session for ${expired.targetName} has expired.`);
        }
      }
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [employeeId, sessions.length, setSessions]);
}
